import React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { ExpandableDescription } from './components/expandable-description/ExpandableDescription';
import { EventDetailsSummary } from './components/event-details-summary/EventDetailsSummary';
import { SpotAvailabilityLine } from './components/event-details-summary/SpotAvailabilityLine';
import { ActionButton } from './components/action-button/ActionButton';
import { UndoActionButton } from './components/undo-action-button/UndoActionButton';
import { BookEventGuestCounter } from './components/book-event/guest-counter/BookEventGuestCounter';
import { BookSpot } from './components/book-spot/BookSpot';
import { BookSpotSuccess } from './components/book-spot/BookSpotSuccess';
import { isSpotBookingEnabledForBookingDetails } from '@glofoxinc/room-layout-map/interactiveSpotBooking';
import type { ComponentType } from 'react';
import {
  resolveMemberSpotFromBookingRecord,
  type RoomLayoutMapProps,
} from '@glofoxinc/room-layout-map/interactiveSpotBooking';
import { LabeledCheckbox } from './components/labeled-checkbox/LabeledCheckbox';
import { TermsCheckboxGroup } from './components/booking-purchase-dialog/terms/TermsCheckboxGroup';
import { BookingPurchaseDialog } from './components/booking-purchase-dialog/BookingPurchaseDialog';
import { BookEventDialog } from './components/booking-purchase-dialog/BookEventDialog';
import { BookAppointmentDialog } from './components/booking-purchase-dialog/BookAppointmentDialog';
import { BuyMembershipDialog } from './components/booking-purchase-dialog/BuyMembershipDialog';
import { MobileHeader } from './components/mobile-header/MobileHeader';
import { HorizontalScrollTabs } from './components/horizontal-scroll-tabs/HorizontalScrollTabs';
import { ModalHeader } from './components/modal-header/ModalHeader';
import { PublicTermsPage } from './components/public-terms/PublicTermsPage';
import { ProfileMembershipList } from './components/profile/ProfileMembershipList';
import { ProfileBookingsList } from './components/profile/ProfileBookingsList';
import { ProfileTransactionsList } from './components/profile/ProfileTransactionsList';
import { ProfileLinkedAccounts } from './components/profile/ProfileLinkedAccounts';
import { setDictionary } from './services/locale';
import { createBookDialogService } from './services/bookDialog';
import { createEagreementsGuardService } from './services/eagreementsGuard';
import { createTermsDialogService } from './services/termsDialog';
import { mapPriceBreakdownResponse } from './services/mapPriceBreakdownResponse';
import { createPriceBreakdownDataService } from './services/priceBreakdownData';
import {
  createBookingDialogData,
  createBookingPurchaseDialogModalService,
  createBuyMembershipDialogData,
} from './services/bookingPurchaseDialogModal';
import { createBookingPurchaseHost } from './services/bookingPurchaseHost';
import { createBuyMembershipHost } from './services/buyMembershipHost';

type WebReactRegistry = {
  __React?: typeof React;
  __ReactDOM?: typeof ReactDOMClient;
  setDictionary?: typeof setDictionary;
  ExpandableDescription?: typeof ExpandableDescription;
  EventDetailsSummary?: typeof EventDetailsSummary;
  SpotAvailabilityLine?: typeof SpotAvailabilityLine;
  ActionButton?: typeof ActionButton;
  UndoActionButton?: typeof UndoActionButton;
  BookEventGuestCounter?: typeof BookEventGuestCounter;
  BookSpot?: typeof BookSpot;
  BookSpotSuccess?: typeof BookSpotSuccess;
  isSpotBookingEnabledForBookingDetails?: typeof isSpotBookingEnabledForBookingDetails;
  RoomLayoutMap?: ComponentType<{ data: RoomLayoutMapProps }>;
  resolveMemberSpotFromBookingRecord?: typeof resolveMemberSpotFromBookingRecord;
  LabeledCheckbox?: typeof LabeledCheckbox;
  TermsCheckboxGroup?: typeof TermsCheckboxGroup;
  BookingPurchaseDialog?: typeof BookingPurchaseDialog;
  BookEventDialog?: typeof BookEventDialog;
  BookAppointmentDialog?: typeof BookAppointmentDialog;
  BuyMembershipDialog?: typeof BuyMembershipDialog;
  MobileHeader?: typeof MobileHeader;
  HorizontalScrollTabs?: typeof HorizontalScrollTabs;
  ModalHeader?: typeof ModalHeader;
  PublicTermsPage?: typeof PublicTermsPage;
  ProfileMembershipList?: typeof ProfileMembershipList;
  ProfileBookingsList?: typeof ProfileBookingsList;
  ProfileTransactionsList?: typeof ProfileTransactionsList;
  ProfileLinkedAccounts?: typeof ProfileLinkedAccounts;
};

type WebReactServices = {
  createBookDialogService: typeof createBookDialogService;
  createEagreementsGuardService: typeof createEagreementsGuardService;
  createTermsDialogService: typeof createTermsDialogService;
  mapPriceBreakdownResponse: typeof mapPriceBreakdownResponse;
  createPriceBreakdownDataService: typeof createPriceBreakdownDataService;
  createBookingPurchaseDialogModalService: typeof createBookingPurchaseDialogModalService;
  createBookingDialogData: typeof createBookingDialogData;
  createBuyMembershipDialogData: typeof createBuyMembershipDialogData;
  createBookingPurchaseHost: typeof createBookingPurchaseHost;
  createBuyMembershipHost: typeof createBuyMembershipHost;
};

declare global {
  interface Window {
    WebReactComponents?: WebReactRegistry;
    WebPortalReactInternalServices?: WebReactServices;
  }
}

function registerGlobals() {
  if (typeof window === 'undefined') {
    return;
  }

  const registry: WebReactRegistry = window.WebReactComponents ?? {};
  window.WebReactComponents = registry;
  registry.__React = React;
  registry.__ReactDOM = ReactDOMClient;
  registry.setDictionary = setDictionary;
  registry.ExpandableDescription = ExpandableDescription;
  registry.EventDetailsSummary = EventDetailsSummary;
  registry.SpotAvailabilityLine = SpotAvailabilityLine;
  registry.ActionButton = ActionButton;
  registry.UndoActionButton = UndoActionButton;
  registry.BookEventGuestCounter = BookEventGuestCounter;
  registry.BookSpot = BookSpot;
  registry.BookSpotSuccess = BookSpotSuccess;
  registry.resolveMemberSpotFromBookingRecord = resolveMemberSpotFromBookingRecord;
  registry.isSpotBookingEnabledForBookingDetails = isSpotBookingEnabledForBookingDetails;
  registry.LabeledCheckbox = LabeledCheckbox;
  registry.TermsCheckboxGroup = TermsCheckboxGroup;
  registry.BookingPurchaseDialog = BookingPurchaseDialog;
  registry.BookEventDialog = BookEventDialog;
  registry.BookAppointmentDialog = BookAppointmentDialog;
  registry.BuyMembershipDialog = BuyMembershipDialog;
  registry.MobileHeader = MobileHeader;
  registry.HorizontalScrollTabs = HorizontalScrollTabs;
  registry.ModalHeader = ModalHeader;
  registry.PublicTermsPage = PublicTermsPage;
  registry.ProfileMembershipList = ProfileMembershipList;
  registry.ProfileBookingsList = ProfileBookingsList;
  registry.ProfileTransactionsList = ProfileTransactionsList;
  registry.ProfileLinkedAccounts = ProfileLinkedAccounts;
  window.WebPortalReactInternalServices = {
    createBookDialogService,
    createEagreementsGuardService,
    createTermsDialogService,
    mapPriceBreakdownResponse,
    createPriceBreakdownDataService,
    createBookingPurchaseDialogModalService,
    createBookingDialogData,
    createBuyMembershipDialogData,
    createBookingPurchaseHost,
    createBuyMembershipHost,
  };
}

registerGlobals();
